'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryApi } from '@/features/query/api';
import { getQueryClient } from '@/shared/api/query-client';
import { isTelephoneValid } from '@/shared/lib/validations';

/** Sync read for submit guards — avoids useWatch(telephone) on large cart widgets. */
export const getCachedTelephoneVerified = (
    phoneNumber: string,
    {
        customerTelephone = null,
        isLoggedIn = false,
        fallbackToLocal = false,
    }: {
        customerTelephone?: string | null;
        isLoggedIn?: boolean;
        fallbackToLocal?: boolean;
    } = {},
): boolean => {
    if (isLoggedIn && customerTelephone === phoneNumber) return true;
    const useGuestFlow = !isLoggedIn || fallbackToLocal;
    if (!useGuestFlow) {
        const status = getQueryClient().getQueryData<{ isVerified?: boolean }>(
            QueryApi.Customer.queries.TelephoneVerificationStatus.queryKey,
        );
        return !!status?.isVerified;
    }
    const guest = getQueryClient().getQueryData<{ isVerified?: boolean }>(
        QueryApi.Order.queries.GuestTelephoneVerificationStatus(phoneNumber).queryKey,
    );
    return !!guest?.isVerified;
};

export const useTelephoneVerification = (
    phoneNumber: string,
    { fallbackToLocal = false, enabled = true }: { fallbackToLocal?: boolean; enabled?: boolean } = {},
) => {
    const customerQuery = useQuery({
        ...QueryApi.Customer.queries.Customer,
        select: (data) => ({
            isLoggedIn: !!data,
            telephone: data?.telephone ? `${data.telephonePrefix} ${data?.telephone}` : null,
        }),
    });
    const { isLoggedIn = false, telephone = null } = customerQuery.data ?? {};

    const isPhoneValid = isTelephoneValid(phoneNumber);

    const useGuestFlow = !isLoggedIn || fallbackToLocal;

    const verificationStatusQuery = useQuery({
        ...QueryApi.Customer.queries.TelephoneVerificationStatus,
        enabled: enabled && isLoggedIn && isPhoneValid && !useGuestFlow,
    });

    const guestVerificationQuery = useQuery({
        ...QueryApi.Order.queries.GuestTelephoneVerificationStatus(phoneNumber),
        enabled: enabled && isPhoneValid && (useGuestFlow || telephone !== phoneNumber),
    });

    const localVerified = guestVerificationQuery.data?.isVerified ?? false;

    const statusQuery = useGuestFlow ? guestVerificationQuery : verificationStatusQuery;
    const isVerified = !enabled
        ? true
        : statusQuery.isLoading
          ? null
          : (isLoggedIn && telephone === phoneNumber) || (useGuestFlow && localVerified);

    const startVerificationMutation = useMutation(
        useGuestFlow
            ? QueryApi.Order.mutations.StartGuestTelephoneVerification
            : QueryApi.Customer.mutations.StartTelephoneVerification,
    );

    const finishGuestMutation = useMutation(QueryApi.Order.mutations.FinishGuestTelephoneVerification);
    const finishUserMutation = useMutation(QueryApi.Customer.mutations.FinishTelephoneVerification);

    const baseFinishMutation = useGuestFlow ? finishGuestMutation : finishUserMutation;
    const finishVerification = {
        ...baseFinishMutation,
        mutateAsync: (verificationCode: string) =>
            baseFinishMutation.mutateAsync({ telephone: phoneNumber, verificationCode }),
    };

    return {
        isVerified,
        nextRetryAt: statusQuery.data?.nextRetryAt,
        isPhoneValid,
        isLoading: enabled ? statusQuery.isLoading : false,
        startVerification: startVerificationMutation,
        finishVerification,
    };
};
